<template>
  <div class="border border-gray-300 rounded-lg overflow-hidden relative">
    <div ref="mapContainer" class="w-full" :class="mapHeightClass"></div>
    <div v-if="geocodeError" class="absolute inset-0 bg-white/80 flex items-center justify-center p-4">
      <div class="text-center">
        <p class="text-sm font-semibold text-red-600">Could not find location</p>
        <p class="text-xs text-gray-600 mt-1">Please try a more specific address.</p>
      </div>
    </div>
    <div v-if="isLoading" class="absolute inset-0 bg-white/80 flex items-center justify-center">
      <div class="flex flex-col items-center">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-newPrimary"></div>
          <p class="mt-2 text-newPrimary text-sm font-medium">Loading map...</p>
        </div>
    </div>
    <div v-if="editable" class="bg-gray-50 p-2 text-sm text-gray-600">
      <span>Drag the pin to refine the location if needed</span>
    </div>
  </div>
</template>


<script setup>
import { ref, onMounted, watch } from 'vue'
import { useGoogleMaps } from '@/composables/useGoogleMaps'

import { Pin } from 'lucide-vue-next'
import config from '@/config'

const props = defineProps({
  lat: { type: Number, default: null },
  lng: { type: Number, default: null },
  address: { type: String, default: '' },
  editable: { type: Boolean, default: false },
  mapHeightClass: { type: String, default: 'h-64' },
  zoom: { type: Number, default: 13 }
})

const emit = defineEmits(['update:location', 'geocode-failed']);
const geocodeError = ref(false);

const { loadScript } = useGoogleMaps()

const mapContainer = ref(null)
const isLoading = ref(true)
let map = null
let marker = null
let geocoder = null
let debounceTimer = null

const initMap = async () => {
  try {
    await loadScript();
    geocoder = new google.maps.Geocoder();
    const position = { lat: props.lat || -33.867, lng: props.lng || 151.205 };

    map = new google.maps.Map(mapContainer.value, {
      zoom: props.zoom,
      center: position,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP
      },
      styles: [
        {
        "featureType": "administrative",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#484848"}]
        },
        {
        "featureType": "landscape",
        "elementType": "all",
        "stylers": [{"color": "#f7f7f7"}]
        },
        {
        "featureType": "poi",
        "elementType": "all",
        "stylers": [{"visibility": "off"}]
        },
        {
        "featureType": "road",
        "elementType": "all",
        "stylers": [{"saturation": -100}, {"lightness": 45}]
        },
        {
        "featureType": "transit",
        "elementType": "all",
        "stylers": [{"visibility": "simplified"}]
        },
        {
        "featureType": "water",
        "elementType": "all",
        "stylers": [{"color": "#0ea5e9"}, {"visibility": "on"}, {"lightness": 70}]
        }
      ]
    });

    marker = new google.maps.Marker({
      position,
      map,
      animation: null,
      draggable: props.editable, // Draggable only if editable
      icon: {
        path: "M12 2C7.9 2 4.5 5.4 4.5 9.5c0 5.2 7.5 11.5 7.5 11.5s7.5-6.3 7.5-11.5C19.5 5.4 16.1 2 12 2Z",
        fillOpacity: 1,
        fillColor: '#00848b',
        scale: 1.5,
        strokeColor: '#006970',
        strokeWeight: 1.5,
        anchor: new google.maps.Point(12, 24),
        zIndex: 100
      }
    });

    if (props.editable) {
      google.maps.event.addListener(marker, 'dragend', (event) => {
        const newLocation = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        };
        emit('update:location', newLocation);
      });
    }

    if (props.address && (!props.lat || !props.lng)) {
      geocodeAddress(props.address);
    } else {
      isLoading.value = false;
    }

    // Only disable loading if not editable. If editable disable loading once geocoding
    if (!props.editable) {
      isLoading.value = false;
    }
  } catch (error) {
    console.error(error);
    isLoading.value = false;
  }
};

const geocodeAddress = (address) => {
  isLoading.value = true;
  geocoder.geocode({ address: address }, (results, status) => {
    if (status === 'OK') {
      const location = results[0].geometry.location;
      map.setCenter(location);
      marker.setPosition(location);
      emit('update:location', { lat: location.lat(), lng: location.lng() });
      geocodeError.value = false
    } else {
      console.error('Geocode was not successful for the following reason: ' + status);
      geocodeError.value = true; // Set the local error state
      emit('geocode-failed'); // EMIT the event to the parent
    }
    isLoading.value = false;
  });
};

const initMap2 = () => {
  if (!mapContainer.value) {
    console.error('Map container not available.');
    isMapLoading.value = false;
    return;
  }

  try {
    const position = { lat: props.lat, lng: props.lng };

    map = new google.maps.Map(mapContainer.value, {
      zoom: 14,
      center: position,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP
      },
      // ... (your map styles and options)
      styles: [
        {
        "featureType": "administrative",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#484848"}]
        },
        {
        "featureType": "landscape",
        "elementType": "all",
        "stylers": [{"color": "#f7f7f7"}]
        },
        {
        "featureType": "poi",
        "elementType": "all",
        "stylers": [{"visibility": "off"}]
        },
        {
        "featureType": "road",
        "elementType": "all",
        "stylers": [{"saturation": -100}, {"lightness": 45}]
        },
        {
        "featureType": "transit",
        "elementType": "all",
        "stylers": [{"visibility": "simplified"}]
        },
        {
        "featureType": "water",
        "elementType": "all",
        "stylers": [{"color": "#0ea5e9"}, {"visibility": "on"}, {"lightness": 70}]
        }
      ]
    });

    marker = new google.maps.Marker({
      position: position,
      map: map,
      animation: null,
      draggable: false,
      icon: {
        path: "M12 2C7.9 2 4.5 5.4 4.5 9.5c0 5.2 7.5 11.5 7.5 11.5s7.5-6.3 7.5-11.5C19.5 5.4 16.1 2 12 2Z",
        fillOpacity: 1,
        fillColor: '#00848b',
        scale: 1.5,
        strokeColor: '#006970',
        strokeWeight: 1.5,
        anchor: new google.maps.Point(12, 24),
        zIndex: 100
      }
    });

    isMapLoading.value = false;

  } catch (error) {
    console.error('Error initializing map:', error);
    isMapLoading.value = false;
  }
};

onMounted(() => {
  initMap();
});

// Watch for prop changes to update the map center
// watch(() => [props.lat, props.lng], (newPosition) => {
//   if (map && marker) {
//     const newLatLng = new google.maps.LatLng(newPosition[0], newPosition[1]);
//     map.setCenter(newLatLng);
//     marker.setPosition(newLatLng);
//   }
// });

// Watch for address changes when in editable mode
watch(() => props.address, (newAddress) => {
  if (props.editable && newAddress) {
    // Clear any existing timer
    clearTimeout(debounceTimer);

    // Set a new timer. The geocode function will only run
    // after the user has stopped typing for 500ms.
    debounceTimer = setTimeout(() => {
      geocodeAddress(newAddress);
    }, 500); // 500ms delay
  }
});
</script>
